import { useState, useEffect } from 'react'

import { Text, Button, theme } from '@hhgtech/hhg-components'
import { Container } from 'components'
import { Text as MBText } from 'components/atoms/text'
import { ContentSponsor } from 'interfaces/types'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'

import { STATIC_PAGES } from '../../../interfaces/constants'
import {
  StyledSponsoredBy,
  StyledStickySponsoredBy,
  StyledStickySponsoredByContainer,
  StyledStickySponsoredByContent,
  StyledStickySponsoredByIcon,
  StyledStickySponsoredPopup,
} from './index.styled'

export type Props = {
  image: string
  isMobile: boolean
  href?: string
  className?: string
}

const SponsoredBy = ({ image, href, className, isMobile }: Props) => {
  const { formatMessage: f } = useIntl()

  return (
    <StyledSponsoredBy className={className} data-type-mobile={isMobile}>
      <Text size="c2" type="bold" color="#3E3F58">
        {f({
          id: 'articlePage.sponsoredBy',
        }).toUpperCase() + ':'}
      </Text>
      <a
        style={{
          flex: 1,
        }}
        href={href}
        target="_blank"
      >
        <img
          loading="lazy"
          src={image}
          style={{
            height: '22px',
            objectPosition: isMobile ? 'right' : 'left',
            objectFit: 'contain',
            paddingLeft: '1rem',
          }}
        />
      </a>
    </StyledSponsoredBy>
  )
}

export type StickyProps = {
  className?: string
  isCollapsed?: boolean
  sponsoredType?: 'article' | 'content'
  sponsoredBy?: ContentSponsor
}
const OldStickySponsoredBy = ({
  className,
  isCollapsed,
  sponsoredType,
  sponsoredBy,
}: StickyProps) => {
  const { formatMessage: f } = useIntl()
  const [showPopup, setShowPopup] = useState(false)
  const router = useRouter()
  const { locale = 'vi-VN' } = router
  useEffect(() => {
    if (isCollapsed) setShowPopup(false)
  }, [isCollapsed])
  return (
    <StyledStickySponsoredBy
      className={className}
      onClick={() => setShowPopup(true)}
      data-show-popup={showPopup && !isCollapsed}
      data-align-left={sponsoredBy && !!sponsoredBy?.logo}
    >
      <Container style={{ position: 'relative' }}>
        {sponsoredType === 'content' ? (
          <Text size="p2" color="#3E3F58">
            <div className="sticky-sponsored-info-icon">
              <img
                loading="lazy"
                src="/svg/info-light.svg"
                style={{
                  width: 24,
                  height: 24,
                  verticalAlign: 'middle',
                  marginRight: '0.3rem',
                  marginBottom: '0.1rem',
                }}
              />
            </div>
            {sponsoredBy && !!sponsoredBy?.logo ? (
              <>
                {' '}
                {f({
                  id: 'articlePage.sponsoredContentBy',
                  defaultMessage: 'Content sponsored by',
                })}{' '}
                <img
                  loading="lazy"
                  style={{ height: 28, width: 'auto', verticalAlign: 'middle' }}
                  src={sponsoredBy?.logo || ''}
                  alt={'sponsored-logo'}
                />
              </>
            ) : (
              f({
                id: 'articlePage.sponsoredContent',
                defaultMessage: 'Sponsored Content',
              })
            )}
          </Text>
        ) : (
          <Text size="p2" color="#3E3F58">
            <div className="sticky-sponsored-info-icon">
              <img
                loading="lazy"
                src="/svg/info-light.svg"
                style={{
                  width: 24,
                  height: 24,
                  verticalAlign: 'middle',
                  marginLeft: '0.3rem',
                  marginBottom: '0.1rem',
                }}
              />
            </div>
            {f({
              id: 'articlePage.sponsoredArticle',
            })}
          </Text>
        )}
        {showPopup && !isCollapsed ? (
          <StyledStickySponsoredPopup
            data-align-left={sponsoredBy && !!sponsoredBy?.logo}
          >
            <Text
              size="p3"
              style={{
                marginBottom: '1rem',
              }}
            >
              {f({
                id: 'articlePage.sponsoredByStickyPopup.content',
              })}
            </Text>
            <div
              className="button-container"
              onClick={
                ((e: any) => {
                  e.stopPropagation()
                }) as () => void
              }
            >
              <Button
                size="md"
                color="secondary"
                onClick={() => setShowPopup(false)}
              >
                {f({
                  id: 'articlePage.sponsoredByStickyPopup.close',
                })}
              </Button>
              <Button
                size="md"
                onClick={() => {
                  router.push(`/static${STATIC_PAGES[locale].PRIVACY_POLICY}`)
                }}
              >
                {f({
                  id: 'articlePage.sponsoredByStickyPopup.policies',
                })}
              </Button>
            </div>
          </StyledStickySponsoredPopup>
        ) : null}
      </Container>
    </StyledStickySponsoredBy>
  )
}

type StickySponsoredByProps = {
  className?: string
  isCollapsed?: boolean
  sponsoredType?: 'article' | 'content'
  sponsoredBy?: ContentSponsor
}
const StickySponsoredBy = ({
  className,
  isCollapsed,
}: StickySponsoredByProps) => {
  const { formatMessage: f } = useIntl()
  const { locale = 'vi-VN', push } = useRouter()

  const [isExpanded, setIsExpanded] = useState<boolean>(!isCollapsed)

  useEffect(() => {
    setIsExpanded(!isCollapsed)
  }, [isCollapsed])

  return (
    <StyledStickySponsoredByContainer
      className={className}
      onClick={() => {
        if (!isExpanded) {
          setIsExpanded(true)
        }
      }}
      data-expanded={isExpanded}
    >
      <StyledStickySponsoredByIcon
        src={isExpanded ? '/svg/close-orange.svg' : '/svg/info-light.svg'}
        onClick={() => {
          if (isExpanded) {
            setIsExpanded(false)
          }
        }}
        data-expanded={isExpanded}
      />
      <StyledStickySponsoredByContent data-expanded={isExpanded}>
        <MBText
          type="bodytext-3"
          color={theme.mbColors.gray}
          className="sponsor-text"
        >
          {f({
            id: 'articlePage.sponsoredArticle',
          })}
        </MBText>

        {isExpanded && (
          <MBText
            type="bodytext-3"
            color={theme.mbColors.gray}
            className="sponsor-explanation"
            onClick={() => {
              push(`/static${STATIC_PAGES[locale].PRIVACY_POLICY}`)
            }}
          >
            {f({
              id: 'articlePage.sponsoredByStickyPopup.content',
            })}
          </MBText>
        )}
      </StyledStickySponsoredByContent>
    </StyledStickySponsoredByContainer>
  )
}

export { SponsoredBy, OldStickySponsoredBy, StickySponsoredBy }
